import { Component, OnInit, Input, OnDestroy, inject } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { combineLatest, Observable, Subscription, take } from 'rxjs';

import { AuthService } from '../../core/services/auth.service';
import { BreadcrumbComponent } from '../../shared/components/breadcrumb/breadcrumb.component';
import { BreadcrumbItem } from '../../shared/components/breadcrumb/breadcrumb-item.interface';
import { LoadingComponent } from '../../shared/components/loading/loading.component';
import { PlanService } from '../../core/services/plan.service';
import { GetPlanDetailResponse, Plan, PlanChangeQntRequest } from '../../core/models/plan.model';
import { ConfirmModalComponent } from '../../shared/components/confirm-modal/confirm-modal.component';
import { ChangePlanModalComponent } from '../../shared/components/change-plan-modal/change-plan-modal.component';
import { PlanHistoryModalComponent } from '../../shared/components/plan-history-modal/plan-history-modal.component';
import { ToastService } from '../../shared/services/toast.service';
import { ActionButtonComponent } from '../../shared/components/action-button/action-button.component';
import { GetCreditCardIconSrcPipe } from '../../shared/pipes/card-icon-src.pipe';
import { PaymentTextPipe } from '../../shared/pipes/payment-text.pipe';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';
import { I18nService } from '../../core/services/i18n.service';
import { OrderService } from '../../core/services/order.service';
import { CreateOrderRequest } from '../../core/models/order.model';
import { User } from '../../core/models/user.model';
import { UserFacade } from '../../core/store/user/user.facade';
import { ErrorDisplayComponent } from '../../shared/components/error-display/error-display.component';
import {
  isTelemedicineProduct,
  TELEMEDICINE_ACCESS_LINK,
  TELEMEDICINE_DEFAULT_PASSWORD,
} from '../../core/utils/telemedicine-product-variations.util';


@Component({
  selector: 'umkt-plan-details',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    BreadcrumbComponent,
    LoadingComponent,
    ConfirmModalComponent,
    ChangePlanModalComponent,
    PlanHistoryModalComponent,
    ActionButtonComponent,
    GetCreditCardIconSrcPipe,
    PaymentTextPipe,
    I18nPipe,
    ErrorDisplayComponent
  ],
  templateUrl: './plan-details.component.html',
  styleUrls: ['./plan-details.component.scss'],
})
export class PlanDetailsComponent implements OnInit, OnDestroy {
  @Input() planUuid?: string; // Novo input para usar como componente embarcado
  @Input() embeddedMode = false; // Flag para modo embarcado

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private authService = inject(AuthService);
  private planService = inject(PlanService);
  private location = inject(Location);
  private toast = inject(ToastService);
  private i18nService = inject(I18nService);
  private orderService = inject(OrderService);
  private userFacade = inject(UserFacade);

  isLoading = true;
  plan: Plan | any;
  breadcrumbItems: BreadcrumbItem[] = [];
  currentUserId: string | null = null;
  showCancelPlanConfirm = false;
  isCanceling = false;
  showPassword = false;
  showTelemedicinePassword = false;
  readonly telemedicinePassword = TELEMEDICINE_DEFAULT_PASSWORD;
  isFixaSelected = false;

  showChangePlanModal = false;
  showPlanHistoryModal = false;

  error: boolean = false;
  retryCount = 0;

  private langSubscription?: Subscription;
  currentPlanUuid!: string;
  currentUser$: Observable<User | null> = this.userFacade.getUserDetails();

  ngOnInit(): void {
    this.currentUserId = this.authService.getCurrentUserUuid();

    // Se é modo embarcado e tem planUuid como input, use o input
    let planUuid = this.planUuid;

    // Se não é modo embarcado, use a lógica original (rota ou service)
    if (!planUuid && !this.embeddedMode) {
      planUuid = this.route.snapshot.params['planUuid'];
      if (!planUuid) {
        planUuid = this.planService.getSelectedPlanId() || '';
      }
    }

    if (!planUuid) {
      if (!this.embeddedMode) {
        this.location.back();
      }
      return;
    }

    // Só mostra breadcrumb se não é modo embarcado
    if (!this.embeddedMode) {
      this.langSubscription = this.i18nService.getCurrentLanguage().subscribe(() => {
        this.updateBreadcrumb();
      });
    }

    combineLatest([
      this.userFacade.getSelectedContract(),
      this.userFacade.getSelectedContractDetails(),
    ])
      .pipe(take(1))
      .subscribe(([contract, details]) => {
        this.setIsFixaSelected(!!contract || !!details);
      });
    this.currentPlanUuid = planUuid;
    this.loadPlan(planUuid);
  }

  private updateBreadcrumb() {
    const previousQueryParams = window.history.state?.previousQueryParams;
    
    const items = [
      { label: this.i18nService.instant('shared.labels.myAccount'), url: '/my-account' },
      { 
        label: this.i18nService.instant('shared.labels.plans'), 
        url: '/my-account/plans',
        queryParams: previousQueryParams && Object.keys(previousQueryParams).length > 0 
          ? previousQueryParams 
          : undefined
      },
      { label: this.i18nService.instant('pages.planDetails.breadcrumb.details'), url: '' },
    ];

    this.breadcrumbItems = items;
  }


  private loadPlan(planUuid: string): void {
    this.isLoading = true;
    this.error = false
    this.planService.getPlanDetailByUuid(planUuid).subscribe({
      next: (resp: GetPlanDetailResponse) => {
        this.plan = resp.data ?? null;
        if (this.plan?.productName) {
          const fullName =
            `${this.plan.productName} ${this.plan.variationName ?? ''}`.trim();

          this.breadcrumbItems = this.breadcrumbItems.map((b, idx) =>
            idx === this.breadcrumbItems.length - 1
              ? { ...b, label: fullName }
              : b
          );
        }
        
        this.retryCount = 0;
        this.isLoading = false;
      },
      error: err => {
        this.error = true
        console.error('Erro ao carregar detalhes do plano', err);
        this.isLoading = false;
      },
    });
  }

  ngOnDestroy(): void {
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
  }

  get isActive(): boolean {
    return (this.plan?.status ?? '').toUpperCase() === 'ACTIVE';
  }

  getMicrosoftDomainFromEmail(email?: string | null): string {
    if (!email) return '-';
    const user = email.split('@')[0] || 'conta';
    return `${user}.onmicrosoft.com`;
  }

  getPlanTermValue(label: string): string {
    if (!this.plan?.planTerms || !Array.isArray(this.plan.planTerms)) {
      return '-';
    }
    const term = this.plan.planTerms.find((t: any) => t.label === label);
    return term?.value || '-';
  }

  getPlanTermOrigin(label: string): string | null {
    if (!this.plan?.planTerms || !Array.isArray(this.plan.planTerms)) {
      return null;
    }
    const term = this.plan.planTerms.find((t: any) => t.label === label);
    return term?.origin || null;
  }

  get microsoftDomain(): string {
    return this.getPlanTermValue('microsoft_domain');
  }

  get initialLogin(): string {
    return this.getPlanTermValue('initial_login');
  }

  get initialPassword(): string {
    return this.getPlanTermValue('initial_password');
  }

  get hasInitialPassword(): boolean {
    return !this.isUnsetPlanTerm(this.initialPassword);
  }

  get userDomain(): string {
    return this.getPlanTermValue('user_domain');
  }

  get activationLink(): string {
    return this.getPlanTermValue('activation_link');
  }

  get hostopiaUsername(): string {
    return this.getPlanTermValue('hostopia_username');
  }

  get portalLink(): string {
    return this.getPlanTermValue('portal_link');
  }

  get shouldShowHostopiaUsername(): boolean {
    if (!this.plan?.planTerms || !Array.isArray(this.plan.planTerms)) {
      return false;
    }
    return !this.isUnsetPlanTerm(this.hostopiaUsername);
  }

  get shouldShowPortalLink(): boolean {
    if (!this.plan?.planTerms || !Array.isArray(this.plan.planTerms)) {
      return false;
    }
    return !this.isUnsetPlanTerm(this.portalLink);
  }

  get domainOrigin(): string | null {
    let origin = this.getPlanTermOrigin('microsoft_domain');

    if (!origin) {
      origin = this.getPlanTermOrigin('initial_login');
    }

    if (!origin) {
      origin = this.getPlanTermOrigin('activation_link');
    }

    return origin;
  }

  get domainOnlyValue(): string {
    const origin = this.domainOrigin;

    if (origin?.toUpperCase() === 'MICROSOFT') {
      return this.microsoftDomain;
    } else if (origin?.toUpperCase() === 'MARKETUP') {
      return this.userDomain;
    } else if (origin?.toUpperCase() === 'PARTNER') {
      return '-';
    }

    return '-';
  }

  get domainDisplayValue(): string {
    let origin = this.getPlanTermOrigin('microsoft_domain');

    if (!origin) {
      origin = this.getPlanTermOrigin('initial_login');
    }

    if (origin?.toUpperCase() === 'MICROSOFT') {
      const domain = this.microsoftDomain;
      return domain !== '-' ? `admin@${domain}` : '-';
    } else if (origin?.toUpperCase() === 'MARKETUP') {
      return this.initialLogin;
    } else if (origin?.toUpperCase() === 'PARTNER') {
      return '-';
    }

    return '-';
  }

  get shouldShowDomainOnly(): boolean {
    if (!this.plan?.planTerms || !Array.isArray(this.plan.planTerms)) {
      return false;
    }

    const origin = this.domainOrigin;

    if (origin?.toUpperCase() === 'MICROSOFT') {
      return this.microsoftDomain !== '-';
    } else if (origin?.toUpperCase() === 'MARKETUP') {
      return this.userDomain !== '-';
    } else if (origin?.toUpperCase() === 'PARTNER') {
      return false;
    }

    return false;
  }

  get shouldShowDomain(): boolean {
    // Só mostra o campo "E-mail de acesso" se tiver planTerms e valores válidos
    if (!this.plan?.planTerms || !Array.isArray(this.plan.planTerms)) {
      return false;
    }

    const origin = this.domainOrigin;

    if (origin?.toUpperCase() === 'MICROSOFT') {
      return this.microsoftDomain !== '-';
    } else if (origin?.toUpperCase() === 'MARKETUP') {
      return this.initialLogin !== '-';
    } else if (origin?.toUpperCase() === 'PARTNER') {
      return false;
    }

    return false;
  }

  get maskedPassword(): string {
    return this.hasInitialPassword ? '••••••••' : '-';
  }

  readonly maskedTelemedicinePassword = '••••••••';

  private isUnsetPlanTerm(termValue: string): boolean {
    return termValue === '-';
  }

  get isTelemedicinePlan(): boolean {
    return isTelemedicineProduct(this.plan?.productName);
  }

  get accessLink(): string {
    if (this.isTelemedicinePlan) {
      return TELEMEDICINE_ACCESS_LINK;
    }

    const origin = this.domainOrigin;

    if (origin?.toUpperCase() === 'MICROSOFT') {
      return 'https://www.office.com/';
    } else if (origin?.toUpperCase() === 'MARKETUP') {
      return 'https://marketup.com/';
    } else if (origin?.toUpperCase() === 'PARTNER') {
      const link = this.activationLink;
      return link !== '-' ? link : '';
    }

    return '';
  }

  get isPartnerOrigin(): boolean {
    return this.domainOrigin?.toUpperCase() === 'PARTNER';
  }

  get shouldShowAccessLink(): boolean {
    if (this.isTelemedicinePlan) {
      return true;
    }

    // Só mostra o link se tiver planTerms, tiver link válido e tiver domínio/email para mostrar
    if (!this.plan?.planTerms || !Array.isArray(this.plan.planTerms)) {
      return false;
    }
    if (this.domainOrigin?.toUpperCase() === 'PARTNER') {
      return this.accessLink !== '';
    }
    return this.accessLink !== '' && this.shouldShowDomain;
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleTelemedicinePasswordVisibility(): void {
    this.showTelemedicinePassword = !this.showTelemedicinePassword;
  }

  onChangePlan(): void {
    console.log('Alterar plano', this.plan?.uuid);
  }

  onChangePaymentMethod(planUuid: string): void {
    this.planService.setSelectedPlanId(planUuid);

    this.router.navigate(['/my-account/plans/details', planUuid, 'change-payment']);
  }

  openClearCartConfirm() {
    this.showCancelPlanConfirm = true;
  }
  openChangePlanModal() {
    this.showChangePlanModal = true;
  }
  

  handleCancelPlanConfirm(): void {
    if (!this.plan?.uuid || this.isCanceling) return;

    this.isCanceling = true;

    this.planService.planCancellation(this.plan.uuid).subscribe({
      next: () => {
        this.toast.showSuccess(this.i18nService.instant('pages.planDetails.messages.cancelSuccess'));
        this.showCancelPlanConfirm = false;
        this.isCanceling = false;
        if (this.plan && this.plan.uuid) {
          this.loadPlan(this.plan.uuid);
        }
      },
      error: err => {
        console.error('Erro ao cancelar assinatura', err);
        const msg =
         err?.error?.msg || err?.error?.errors?.[0] ||
         this.i18nService.instant('pages.planDetails.messages.cancelError');

        this.toast.showError(msg);
        this.isCanceling = false;
      },
    });
  }

  handleCancelPlanClose() {
    if (this.isCanceling) return;
    this.showCancelPlanConfirm = false;
  }

  handleRetry() {
    this.retryCount++;
    this.loadPlan(this.currentPlanUuid);
  }

  get cancelModalMessage(): string {
    const expiration = this.plan?.expirationDate
      ? new Date(this.plan.expirationDate).toLocaleDateString('pt-BR')
      : '-';

    if (!this.plan?.hasFidelity) {
      return this.i18nService.instant('pages.planDetails.modal.messageNormal', { date: expiration });
    } 
    return this.i18nService.instant('pages.planDetails.modal.messageFidelity', { date: expiration });
  }

  get canShowChangePlanButton(): boolean {
    const parent = this.plan?.parentProduct;
    if (!parent?.uuid) return false;

    return this.isActive && (this.canChangePlan || this.canChangeQuantity);
  }

  get canChangePlan(): boolean {
    return !this.isFixaSession && !!this.plan?.parentProduct?.acceptUpdate;
  }

  get canChangeQuantity(): boolean {
    return this.isFixaSession || !!this.plan?.parentProduct?.acceptUpdateQuantity;
  }

  get isFixaSession(): boolean {
    return this.authService.isFixaSession() || this.isFixaSelected;
  }

  setIsFixaSelected(value: boolean): void {
    this.isFixaSelected = value;
  }

  handleQuantityUpdate(newQuantity: number): void {
    if (!this.plan?.uuid) return;

    this.isLoading = true; 
    
    const payload: PlanChangeQntRequest = {
      quantity: newQuantity.toString()
      // msisdn: []
    };

    this.orderService.changePlanQnt(this.plan.uuid, payload).subscribe({
      next: () => {
        this.toast.showSuccess(this.i18nService.instant('pages.planDetails.ChangePlanModal.updateQuantitySuccessMsg'));
        this.showChangePlanModal = false;
        this.loadPlan(this.plan!.uuid!);
      },
      error: () => {
        this.isLoading = false;
        this.toast.showError(this.i18nService.instant('pages.planDetails.ChangePlanModal.updateQuantityErrorMsg'));
      }
    });
  }

  private getCurrentUser(): User | null {
      let user: User | null = null;
      this.currentUser$.subscribe(u => (user = u)).unsubscribe();
      return user;
  }

  private buildUpgradeRequiredFields() {
    if (!this.domainOnlyValue) return [];

    return [
      {
        name: 'google_ws_domain',
        label: 'Domínio Google Workspace',
        type: 'TEXT',
        value: this.domainOnlyValue
      },
      {
        name: 'google_ws_admin_email',
        label: 'Admin',
        type: 'TEXT',
        value: 'admin'
      }
    ];
  }

  //todo: Validar payload
  handlePlanUpdate(selectedPlan: any): void {
    if (!this.plan?.uuid || !selectedPlan?.uuid) return;

    const user = this.getCurrentUser();
    if (!user) {
      return;
    }

    const primaryAddress =
      user.addresses?.find((a: any) => a.main) ?? user.addresses?.[0];

    if (!primaryAddress) return;

    const quantity = this.plan.quantity || 1;
    const unitPrice = selectedPlan.price || 0;
    const totalAmount = unitPrice * quantity;

    this.isLoading = true;

    const payload: CreateOrderRequest = {
      customerUuid: user.uuid,
      salespersonUuid: 'ceaf758a-de77-429f-b2f7-071984372deb',

      fluxo: 'BOLETO_CARTAO',

      cardToken: this.plan.cardToken || null,
      msisdn: user.phone || '',

      products: [
        {
          uuid: selectedPlan.uuid,
          quantity: quantity, 
          variable: true,
          coupon: '',
          requiredFields: this.buildUpgradeRequiredFields(),
          origin: 'Odin',
          terms: selectedPlan.terms || '',
          msisdn: []
        }
      ],

      payments: [
        {
          uuid: 'd734b11b-487e-4ff0-a6c6-7fbde4b67af1',
          amount: totalAmount,
          totalInstallments: quantity, 
          paymentNotes: ''
        }
      ],

      billingAddress: {
        address: primaryAddress.address || '',
        number: primaryAddress.number || '',
        zip: primaryAddress.zipCode || '',
        neighborhood: primaryAddress.neighborhood || '',
        city: primaryAddress.city || '',
        country: primaryAddress.country || 'Brasil',
        state: primaryAddress.state || '',
        complement: primaryAddress.complement || '',
      },

      urlDomain: window.location.origin,

      ordersEmailRequest: {
        domainEmail: 'CLARO'
      },

      sellerCode: null,
      recurring: false,

      acceptanceTermId: '',
      ipAddress: '',

      customerAccountId: null,
      operatorCode: null,
      contractId: null
    };

    this.orderService
      .createOrder(payload, 'upgrade', this.plan.uuid) 
      .subscribe({
        next: () => {
          this.toast.showSuccess(this.i18nService.instant('pages.planDetails.ChangePlanModal.upgradePlanSuccessMsg'));
          this.showChangePlanModal = false;
          setTimeout(() => this.loadPlan(this.plan!.uuid!), 2000);
        },
        error: (err) => {
          this.isLoading = false;
          this.toast.showError(this.i18nService.instant('pages.planDetails.ChangePlanModal.upgradePlanErrorMsg'));
          console.error('Erro no Upgrade:', err);
        }
      });
  }

}
